import React, { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth, formatApiError } from "../lib/auth";
import { LogIn, MailCheck, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // When backend returns 403 with code=email_not_verified, show resend UI
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendInfo, setResendInfo] = useState("");
  const [resendDevLink, setResendDevLink] = useState("");

  if (!loading && user) return <Navigate to="/home" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setUnverifiedEmail(null);
    try {
      await login(email, password);
      navigate("/home");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "email_not_verified") {
        setUnverifiedEmail(detail.email || email);
        setError("");
      } else {
        setError(formatApiError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const doResend = async () => {
    if (!unverifiedEmail) return;
    setResendBusy(true);
    setResendInfo("");
    setResendDevLink("");
    try {
      const { data } = await axios.post(`${API}/auth/resend-verification`, {
        email: unverifiedEmail,
      });
      if (data.email_sent) {
        setResendInfo("Verification email sent. Check your inbox.");
      } else if (data.verification_link_dev) {
        setResendInfo("Email service not yet configured. Use the dev link below.");
        setResendDevLink(data.verification_link_dev);
      } else {
        setResendInfo("If your email is unverified, we've sent a new link.");
      }
    } catch (err) {
      setResendInfo(formatApiError(err));
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-slate-50 to-slate-200">
      <Card className="w-full max-w-md shadow-lg" data-testid="login-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <LogIn className="h-5 w-5" /> Sign In
          </CardTitle>
          <p className="text-sm text-slate-500">ZAMBA Product Selector</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
              />
              <div className="mt-1 text-right">
                <Link
                  to="/forgot-password"
                  className="text-xs text-muted-foreground hover:text-foreground"
                  data-testid="login-forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            {/* Unverified-email panel */}
            {unverifiedEmail && (
              <div
                className="bg-amber-50 border border-amber-200 text-amber-900 rounded p-3 space-y-2 text-sm"
                data-testid="login-unverified"
              >
                <div className="flex items-start gap-2">
                  <MailCheck className="h-4 w-4 mt-0.5 flex-none" />
                  <div>
                    Your email <b className="break-all">{unverifiedEmail}</b> is not
                    verified yet. Please check your inbox for the link, or request a
                    new one.
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={doResend}
                  disabled={resendBusy}
                  data-testid="login-resend-btn"
                >
                  {resendBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                  Resend verification email
                </Button>
                {resendInfo && (
                  <div className="text-xs text-slate-700 break-all" data-testid="login-resend-info">
                    {resendInfo}
                  </div>
                )}
                {resendDevLink && (
                  <div
                    className="text-xs bg-white border border-amber-200 rounded p-2 break-all"
                    data-testid="login-resend-dev-link"
                  >
                    <a href={resendDevLink} className="underline">
                      {resendDevLink}
                    </a>
                  </div>
                )}
              </div>
            )}

            {error && !unverifiedEmail && (
              <div
                className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded"
                data-testid="login-error"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="login-submit"
            >
              {busy ? "Signing in…" : "Sign In"}
            </Button>
            <div className="text-center text-sm text-slate-600">
              No account?{" "}
              <Link to="/register" className="text-slate-900 underline" data-testid="goto-register">
                Create one
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
